import PropTypes from 'prop-types'
import { useState } from 'react'
import { connect } from 'react-redux'
import ReactSelect from 'react-select'

import BarcodeIcon from '../../assets/BarcodeIcon'
import TrashIcon from '../../assets/TrashIcon'
import UpdateButton from '../../components/UpdateButton/UpdateButton'
import BarcodeScanner from '../../components/ZXingScanner/BarcodeScanner'

import { PRODUCT_DETAIL_FIELDS } from '../../common/constant/page-constant'
import { useFEFOBatches } from '../../common/hooks/useFEFOBatches'
import { useProductSearch } from '../../common/hooks/useProductSearch'
import { formatDate } from '../../common/utils/appUtils'
import { getTotalAvailableStock } from '../../common/utils/productUtils'

function Product({
  product,
  updateProductDetails,
  handleUpdateProductSubmit,
  handleProductUpdate,
  getProductDetailsByBarcode,
  clearProduct,
  isFieldErrorVisible,
  isBarcodeDisabled,
  isUpdateProductLoading,
}) {
  const {
    searchTerm,
    setSearchTerm,
    searchSuggestions,
    setSelectedSearchTerm,
  } = useProductSearch(updateProductDetails, handleProductUpdate)

  const [isScannerVisible, setIsScannerVisible] = useState(false)
  const selectedBatches = useFEFOBatches(product)

  const handleScan = (result) => {
    if (result) {
      handleProductUpdate(
        product?.product_id,
        PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE,
        result
      )
      setIsScannerVisible(false)
      getProductDetailsByBarcode(result)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    handleUpdateProductSubmit(product)
  }

  const totalStock = getTotalAvailableStock(product?.inventory)

  return (
    <form onSubmit={handleSubmit} className="mt-5">
      {/* === Product Remove Action === */}
      {clearProduct && (
        <div className="addedProductAction dFlex jcSpaceBetween aiCenter">
          <div></div>
          <div className="trashIcon" onClick={() => clearProduct()}>
            <TrashIcon />
          </div>
        </div>
      )}
      {/* === Barcode Input === */}
      <div className="formSection">
        <div className="formGroup formFloating fgrow1">
          <input
            type="text"
            className="formControl"
            id="text"
            placeholder="Product Barcode/QR Code"
            value={product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE] || ''}
            onChange={(e) =>
              handleProductUpdate(
                product?.product_id,
                PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE,
                e.target.value
              )
            }
            onBlur={() => getProductDetailsByBarcode()}
            disabled={isBarcodeDisabled}
            required
          />
          <label htmlFor="barcode">Product Barcode/QR Code</label>
          {isFieldErrorVisible &&
            !product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE] && (
              <div className="invalidFeedback">
                Please enter a valid Product Barcode/QR Code.
              </div>
            )}

          <div
            className="positionedIcon"
            onClick={() => setIsScannerVisible(!isScannerVisible)}
          >
            <BarcodeIcon />
          </div>
          {isScannerVisible && <BarcodeScanner onScan={handleScan} />}
        </div>
      </div>
      {/* === Product Search === */}
      <div className="formGroup formFloating">
        <ReactSelect
          className="formSelect reactSelect"
          id="floatingSelect"
          value={
            product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME]
              ? {
                  id: product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_UNDERSCORE_ID],
                  label: product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME],
                  dose: product?.[PRODUCT_DETAIL_FIELDS.DOSE],
                  category: product?.[PRODUCT_DETAIL_FIELDS.MEDICAL_CATEGORY],
                }
              : null
          }
          isDisabled={isBarcodeDisabled}
          onInputChange={(val, { action }) => {
            if (action === 'input-change') {
              setSearchTerm(val)
            }
          }}
          onChange={(option) => {
            if (option) {
              setSelectedSearchTerm(option.id)

              setSearchTerm('')
            } else {
              setSearchTerm('')
            }
          }}
          onMenuClose={() => {
            setSearchTerm('')
          }}
          options={searchSuggestions}
          getOptionLabel={(option) =>
            `${option.label} ${option.dose || ''} ${
              option.category ? `(${option.category})` : ''
            }`
          }
          getOptionValue={(option) => option.id}
          placeholder="Product Name"
          filterOption={null}
          isClearable={true}
          controlShouldRenderValue={true}
          styles={{
            control: (base) => ({
              ...base,
              border: 0,
              paddingTop: '20px',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused
                ? 'rgb(222, 235, 255)'
                : state.isSelected
                  ? '#FFF'
                  : 'inherit',
              color: '#212529',
            }),
          }}
        />
        <label htmlFor="floatingSelect">Product Name</label>
        {isFieldErrorVisible &&
          !product?.[PRODUCT_DETAIL_FIELDS.PRODUCT_NAME] && (
            <div className="invalidFeedback">Please enter Product Name.</div>
          )}
      </div>

      {/* === Batch Selection === */}
      <div className="formGroup formFloating">
        <ReactSelect
          className="formSelect reactSelect"
          id="floatingSelect"
          value={product?.[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS] || ''}
          onChange={(option) => {
            handleProductUpdate(
              product?.product_id,
              'product_batch_inventory_id',
              option?.product_batch_inventory_id
            )
            handleProductUpdate(
              product?.product_id,
              PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS,
              option
            )
          }}
          options={product?.inventory || []}
          getOptionLabel={(option) =>
            `Batch: ${option?.[PRODUCT_DETAIL_FIELDS.BATCH_NUMBER]}, Expiry: ${formatDate(
              option?.[PRODUCT_DETAIL_FIELDS.EXPIRY_DATE]
            )}`
          }
          placeholder="Select Product Batch"
          styles={{
            control: (base) => ({
              ...base,
              border: 0,
              paddingTop: '20px',
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isFocused
                ? 'rgb(222, 235, 255)'
                : state.isSelected
                  ? '#FFF'
                  : 'inherit',
              color: '#212529',
            }),
          }}
        />
        <label htmlFor="floatingSelect">Select Product Batch</label>
        {isFieldErrorVisible &&
          !product?.[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]?.id && (
            <div className="invalidFeedback">Please Select Product Batch.</div>
          )}
        {product?.[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS] && (
          <div className="inputSuggestionsWrap">
            <ul className="inputSuggestion dFlex">
              <li className="suggestion">
                Selected Batch Stock:{' '}
                {
                  product?.[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]?.[
                    PRODUCT_DETAIL_FIELDS.STOCK
                  ]
                }
              </li>
            </ul>
          </div>
        )}
      </div>
      {/* === Quantity and Dose === */}
      <div className="formInline dFlex flexNowrap">
        <div className="formGroup formFloating fgrow1">
          <input
            type="text"
            className="formControl"
            id="quantity"
            placeholder="Quantity"
            value={product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] || ''}
            onChange={(e) =>
              handleProductUpdate(
                product?.product_id,
                PRODUCT_DETAIL_FIELDS.QUANTITY,
                e.target.value
              )
            }
            required
          />
          <label htmlFor="Quantity">Quantity</label>
          {isFieldErrorVisible &&
            (!product?.[PRODUCT_DETAIL_FIELDS.QUANTITY] ||
              parseInt(product?.[PRODUCT_DETAIL_FIELDS.QUANTITY]) < 1 ||
              parseInt(product?.[PRODUCT_DETAIL_FIELDS.QUANTITY]) >
                totalStock) && (
              <div className="invalidFeedback">
                Please select a valid Quantity (Max: {totalStock}).
              </div>
            )}
        </div>

        <div className="formGroup formFloating ml-4 fgrow1">
          <input
            type="text"
            className="formControl"
            id="dose"
            placeholder="Dose"
            value={product?.[PRODUCT_DETAIL_FIELDS.DOSE] || ''}
            disabled
            required
          />
          <label htmlFor="Dose">Dose</label>
        </div>
      </div>
      {/* === Cost & Rate === */}
      <div className="formInline dFlex flexNowrap">
        <div className="formGroup formFloating formInputText fgrow1">
          <div className="preInputGroup backColor">
            <div className="preInputText">BDT</div>
          </div>
          <input
            type="text"
            className="formControl"
            id="costUnit"
            placeholder="Cost/Unit"
            value={
              product?.[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]?.[
                PRODUCT_DETAIL_FIELDS.PRODUCT_COST
              ] || ''
            }
            disabled
            required
          />
          <label htmlFor="costUnit">Cost/Unit</label>
        </div>

        <div className="formGroup formFloating formInputText ml-4 fgrow1">
          <div className="preInputGroup backColor">
            <div className="preInputText">BDT</div>
          </div>
          <input
            type="text"
            className="formControl"
            id="rateUnit"
            placeholder="Rate/Unit"
            value={
              product?.[PRODUCT_DETAIL_FIELDS.SELECTED_BATCH_DETAILS]?.[
                PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT
              ] || ''
            }
            disabled
            required
          />
          <label htmlFor="rateUnit">Rate/Unit</label>
        </div>
      </div>
      {/* === Update Button === */}
      {product?.id && <UpdateButton isLoading={isUpdateProductLoading} />}
    </form>
  )
}

Product.propTypes = {
  product: PropTypes.object,
  isFieldErrorVisible: PropTypes.bool,
  handleProductUpdate: PropTypes.func,
  updateProductDetails: PropTypes.func,
  handleUpdateProductSubmit: PropTypes.func,
  getProductDetailsByBarcode: PropTypes.func,
  clearProduct: PropTypes.func,
  isBarcodeDisabled: PropTypes.bool,
  isUpdateProductLoading: PropTypes.bool,
}
export default connect(null, null)(Product)
